import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth, API } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, Gavel, Trophy, FileText, Download, Truck, Share2, Copy, Link2Off } from "lucide-react";
import VendorBadges from "@/components/VendorBadges";

// Rendered as dedicated sections (category tag, SKU table) rather than the generic
// "STRUCTURED REQUIREMENTS" grid, which previously caused duplicate display and the
// "[object Object]" bug when requirements.skus held an array of objects.
const EXCLUDED_REQUIREMENT_KEYS = new Set(["skus", "sector", "sectors", "category", "category_id"]);

// Manufacturers should see a neutral, system-generated reference instead of the
// brand name the Brand Builder published, so the brand stays unrevealed pre-award.
const rfqDisplayCode = (rfqId) => `RFQ-${(rfqId || "").replace(/^rfq_/, "").toUpperCase()}`;

export default function RFQDetail() {
  const { rfqId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [bidEval, setBidEval] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  const [accepting, setAccepting] = useState("");
  const [passing, setPassing] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState("");
  const [trackingUrl, setTrackingUrl] = useState("");
  const [shareToken, setShareToken] = useState(null);
  const [shareBusy, setShareBusy] = useState(false);
  const [quote, setQuote] = useState({
    price_usd: "", lead_time_days: "", message: "", contact_number: "",
    sample_available: false, sample_pricing_mode: "standard", sample_cost_usd: "", sample_lead_time_days: "",
    sku_quotes: [],
  });
  const [shortlisting, setShortlisting] = useState("");
  const [samples, setSamples] = useState([]);
  const [sampleBusy, setSampleBusy] = useState("");

  const load = useCallback(() => axios.get(`${API}/rfqs/${rfqId}`).then((r) => {
    setData(r.data);
    setShareToken(r.data?.rfq?.share_token || null);
  }), [rfqId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (user?.role !== "buyer") return;
    axios.get(`${API}/samples`).then((response) => setSamples(response.data || [])).catch(() => {});
  }, [rfqId, user?.role]);

  if (!data) return <div className="min-h-screen bg-bone"><Navbar /><div className="p-20 text-center font-mono">Loading…</div></div>;
  const { rfq, quotes, buyer_name, buyer_anonymised } = data;
  // Top-level rfq.skus is the canonical field; fall back to requirements.skus for
  // older RFQs created before the dedicated field existed.
  const skuList = (rfq.skus?.length ? rfq.skus : rfq.requirements?.skus) || [];
  const RESERVED_SKU_KEYS = new Set(["sku", "product_name", "quantity"]);
  // Prefer the columns snapshot taken at submission time so the table matches
  // exactly what the Brand Builder filled in. Fall back to deriving columns
  // from whatever keys are present on older RFQs saved before this snapshot existed.
  const skuColumns = rfq.sku_columns?.length
    ? rfq.sku_columns
    : Array.from(
        skuList.reduce((keys, row) => {
          Object.keys(row || {}).forEach((key) => { if (!RESERVED_SKU_KEYS.has(key)) keys.add(key); });
          return keys;
        }, new Set()),
      ).map((key) => ({ key, label: key.replaceAll("_", " ") }));

  const passRfq = async () => {
    if (!window.confirm("Pass on this RFQ? It will be removed from your incoming list.")) return;
    setPassing(true);
    try {
      await axios.post(`${API}/rfqs/${rfqId}/pass`);
      toast.success("Passed");
      nav("/rfqs");
    } catch (err) {
      toast.error("Failed");
    } finally { setPassing(false); }
  };

  const generateShare = async () => {
    setShareBusy(true);
    try {
      const r = await axios.post(`${API}/rfqs/${rfqId}/share`);
      setShareToken(r.data.share_token);
      const url = `${window.location.origin}/p/rfq/${r.data.share_token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Public link copied to clipboard");
      } catch {
        toast.success("Public link generated");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setShareBusy(false); }
  };

  const copyShare = async () => {
    if (!shareToken) return;
    const url = `${window.location.origin}/p/rfq/${shareToken}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  const revokeShare = async () => {
    if (!window.confirm("Revoke the public link? Anyone with the URL will lose access.")) return;
    setShareBusy(true);
    try {
      await axios.delete(`${API}/rfqs/${rfqId}/share`);
      setShareToken(null);
      toast.success("Public link revoked");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setShareBusy(false); }
  };

  const updateStatus = async (quoteId, nextStatus, tracking = "") => {
    setStatusUpdating(quoteId);
    try {
      await axios.post(`${API}/quotes/${quoteId}/status`, { status: nextStatus, tracking_url: tracking });
      toast.success(`Marked as ${nextStatus}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setStatusUpdating(""); }
  };

  const evaluateBids = async () => {
    setEvaluating(true);
    try {
      const r = await axios.post(`${API}/rfqs/${rfqId}/evaluate-bids`);
      setBidEval(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Evaluation failed");
    } finally { setEvaluating(false); }
  };

  const requestSample = async (quote) => {
    setSampleBusy(quote.quote_id);
    try {
      const response = await axios.post(`${API}/samples`, {
        rfq_id: rfqId,
        quote_id: quote.quote_id,
        description: "Buyer-requested sample",
        quantity: 1,
      });
      setSamples((current) => [
        ...current.filter((sample) => sample.quote_id !== quote.quote_id),
        response.data,
      ]);
      toast.success("Sample request sent to the manufacturer");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to request sample");
    } finally { setSampleBusy(""); }
  };

  const submitQuote = async (e) => {
    e.preventDefault();

    let priceUsd = Number(quote.price_usd) || 0;
    let leadTimeDays = Number(quote.lead_time_days) || 0;
    let skuQuotes = [];

    if (skuList.length > 0) {
      // Per-SKU pricing is mandatory: every SKU row needs its own price and
      // lead time. Overall price/lead time shown elsewhere are derived from
      // these (qty-weighted average price, worst-case lead time) rather than
      // asked for separately.
      skuQuotes = skuList.map((s, i) => quote.sku_quotes[i] || {});
      const missing = skuQuotes.some((row) => row.price_usd === "" || row.price_usd == null || row.lead_time_days === "" || row.lead_time_days == null);
      if (missing) {
        toast.error("Enter a price per unit and lead time for every SKU");
        return;
      }
      if (quote.sample_available && quote.sample_pricing_mode === "per_sku") {
        const missingSample = skuQuotes.some((row) => row.sample_cost_usd === "" || row.sample_cost_usd == null);
        if (missingSample) {
          toast.error("Enter a sample cost for every SKU, or switch to a standard sample cost");
          return;
        }
      }
      const totalQty = skuList.reduce((sum, s) => sum + (Number(s.quantity) || 0), 0) || skuList.length;
      priceUsd = skuList.reduce((sum, s, i) => sum + (Number(skuQuotes[i]?.price_usd) || 0) * (Number(s.quantity) || 1), 0) / totalQty;
      leadTimeDays = Math.max(...skuQuotes.map((row) => Number(row.lead_time_days) || 0));
      skuQuotes = skuQuotes.map((row, i) => ({
        sku: skuList[i].sku || "",
        price_usd: Number(row.price_usd),
        lead_time_days: Number(row.lead_time_days),
        notes: row.notes || "",
        ...(quote.sample_available && quote.sample_pricing_mode === "per_sku" ? { sample_cost_usd: Number(row.sample_cost_usd) } : {}),
      }));
    }

    if (quote.sample_available && quote.sample_pricing_mode === "standard") {
      if (quote.sample_cost_usd === "" || quote.sample_lead_time_days === "") {
        toast.error("Enter a standard sample cost and lead time, or switch to per-SKU sample pricing");
        return;
      }
    }

    try {
      await axios.post(`${API}/rfqs/${rfqId}/quotes`, {
        price_usd: priceUsd,
        lead_time_days: leadTimeDays,
        message: quote.message,
        contact_number: "",
        sample_available: !!quote.sample_available,
        sample_pricing_mode: quote.sample_pricing_mode,
        sample_cost_usd: quote.sample_pricing_mode === "standard" ? Number(quote.sample_cost_usd) || 0 : 0,
        sample_lead_time_days: quote.sample_pricing_mode === "standard" && quote.sample_lead_time_days !== "" ? Number(quote.sample_lead_time_days) : null,
        sku_quotes: skuQuotes,
      });
      toast.success("Quote submitted");
      setQuote({
        price_usd: "", lead_time_days: "", message: "", contact_number: "",
        sample_available: false, sample_pricing_mode: "standard", sample_cost_usd: "", sample_lead_time_days: "",
        sku_quotes: [],
      });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const acceptQuote = async (quoteId) => {
    if (!window.confirm("Accept this quote and close the RFQ?")) return;
    setAccepting(quoteId);
    try {
      await axios.post(`${API}/rfqs/${rfqId}/accept`, { quote_id: quoteId });
      toast.success("Winner accepted. RFQ closed.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setAccepting(""); }
  };

  const toggleShortlist = async (quoteId, shortlisted) => {
    setShortlisting(quoteId);
    try {
      await axios.post(`${API}/rfqs/${rfqId}/quotes/${quoteId}/shortlist`, { shortlisted });
      toast.success(shortlisted ? "Quotation shortlisted" : "Removed from shortlist");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setShortlisting(""); }
  };

  const isBuyer = user?.role === "buyer" && user?.user_id === rfq.buyer_id;
  const isAgent = user?.role === "agent";
  const biddingOpen = rfq.status === "open" && (rfq.bidding_status || "active") === "active"
    && (!rfq.bidding_deadline || new Date(rfq.bidding_deadline) > new Date());

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16">
        <Link to={user?.role === "admin" ? "/admin" : "/rfqs"} className="font-mono text-xs underline mb-8 block">
          {user?.role === "admin" ? "← BACK TO ADMIN" : "← BACK TO RFQs"}
        </Link>
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <span className="tag">{rfq.category}</span>
          <span className="tag">{rfq.target_region}</span>
          <span className={`tag ${rfq.status === "open" ? "verified" : ""}`}>{rfq.status}</span>
        </div>
        <h1 className="font-serif text-5xl lg:text-6xl font-light leading-none tracking-tight mb-6" data-testid="rfq-heading">
          {isAgent ? rfqDisplayCode(rfq.rfq_id) : rfq.title}
        </h1>
        <div className="font-mono text-xs text-[--muted-foreground] mb-8">FROM {buyer_name?.toUpperCase()}{buyer_anonymised ? " · IDENTITY HIDDEN UNTIL RFQ CLOSES" : ""} · {new Date(rfq.created_at).toLocaleDateString()}</div>

        {isBuyer && (
          <div className="editorial-card p-6 mb-8" data-testid="rfq-share-panel">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-start gap-4">
                <Share2 className="w-5 h-5 text-klein mt-1" />
                <div>
                  <div className="overline mb-1">§ PUBLIC LINK</div>
                  <div className="font-serif text-2xl tracking-tight leading-tight">
                    {shareToken ? "Share this RFQ anywhere" : "Generate a shareable link"}
                  </div>
                  <p className="text-sm text-[--muted-foreground] mt-1 max-w-md">
                    {shareToken
                      ? "A read-only public link. Buyer identity, attachments, and bidder info stay hidden."
                      : "Post on LinkedIn, WhatsApp, email — anyone can read the brief without signing in."}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {shareToken ? (
                  <>
                    <button onClick={copyShare} className="btn-primary text-xs" data-testid="copy-share-link">
                      <Copy className="w-3 h-3" /> Copy link
                    </button>
                    <button onClick={revokeShare} disabled={shareBusy} className="btn-outline text-xs" data-testid="revoke-share-link">
                      <Link2Off className="w-3 h-3" /> Revoke
                    </button>
                  </>
                ) : (
                  <button onClick={generateShare} disabled={shareBusy} className="btn-primary text-xs" data-testid="generate-share-link">
                    <Share2 className="w-3 h-3" /> {shareBusy ? "Generating…" : "Generate link"}
                  </button>
                )}
              </div>
            </div>
            {shareToken && (
              <div className="mt-4 pt-4 border-t border-[--border-soft] font-mono text-xs text-[--muted-foreground] break-all" data-testid="share-link-text">
                {`${window.location.origin}/p/rfq/${shareToken}`}
              </div>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Stat l="Quantity" v={rfq.quantity} />
          <Stat l="Budget" v={`₹${Number(rfq.budget_usd).toLocaleString()}`} />
          <Stat l="Timeline" v={rfq.timeline} />
        </div>

        <div className="editorial-card p-8 mb-12">
          <div className="overline mb-4">§ BRIEF</div>
          <p className="text-base leading-relaxed whitespace-pre-wrap">{rfq.description}</p>
        </div>

        {/* skus/sector/sectors/category are rendered as dedicated sections below and
            excluded here to avoid duplicate display and the raw-object "[object Object]" bug. */}
        {rfq.requirements && Object.keys(rfq.requirements).filter((k) => !EXCLUDED_REQUIREMENT_KEYS.has(k)).length > 0 && (
          <div className="editorial-card p-8 mb-12" data-testid="rfq-requirements">
            <div className="overline mb-5">§ STRUCTURED REQUIREMENTS</div>
            <div className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
              {Object.entries(rfq.requirements)
                .filter(([k, v]) => !EXCLUDED_REQUIREMENT_KEYS.has(k) && v && (Array.isArray(v) ? v.length : true))
                .map(([k, v]) => (
                  <div key={k}>
                    <div className="overline text-[10px] mb-1">{k.replace(/_/g, " ")}</div>
                    <div className="text-base">
                      {Array.isArray(v)
                        ? v.map((item) => (item && typeof item === "object" ? Object.values(item).filter(Boolean).join(" · ") : String(item))).join(", ")
                        : (v && typeof v === "object" ? Object.values(v).filter(Boolean).join(" · ") : String(v))}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {skuList.length > 0 && (
          <div className="editorial-card p-8 mb-12" data-testid="rfq-skus">
            <div className="overline mb-5">§ PRODUCTS / SKUs ({skuList.length})</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[--border-soft] text-left font-mono text-[10px] uppercase tracking-wider text-[--muted-foreground]">
                    <th className="p-3">SKU</th>
                    <th className="p-3">Product</th>
                    <th className="p-3">Qty</th>
                    {skuColumns.map((column) => <th key={column.key} className="p-3">{column.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {skuList.map((s, i) => (
                    <tr key={s.sku || i} className="border-b border-[--border-soft] align-top" data-testid={`sku-row-${i}`}>
                      <td className="p-3 font-mono">{s.sku || "—"}</td>
                      <td className="p-3">{s.product_name || "—"}</td>
                      <td className="p-3">{s.quantity || "—"}</td>
                      {skuColumns.map((column) => <td key={column.key} className="p-3">{s[column.key] || "—"}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {rfq.requirements_text && rfq.requirements_text.trim() && (
          <div className="editorial-card p-8 mb-12" data-testid="rfq-requirements-text">
            <div className="overline mb-5">§ DETAILED REQUIREMENTS</div>
            <div className="prose max-w-none">
              <pre className="whitespace-pre-wrap text-base leading-relaxed font-sans">{rfq.requirements_text}</pre>
            </div>
          </div>
        )}

        {(rfq.attachments || []).length > 0 && (
          <div className="editorial-card p-8 mb-12" data-testid="rfq-attachments">
            <div className="overline mb-5">§ ATTACHMENTS ({rfq.attachments.length})</div>
            <div className="space-y-2">
              {rfq.attachments.map((a) => (
                <a
                  key={a.file_id}
                  href={`${API}/rfqs/${rfq.rfq_id}/attachments/${a.file_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between p-4 border border-[--border-soft] hover:border-ink transition-colors"
                  data-testid={`attachment-link-${a.file_id}`}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-klein" />
                    <div>
                      <div className="font-serif text-lg leading-tight">{a.filename}</div>
                      <div className="font-mono text-[10px] text-[--muted-foreground]">{(a.size / 1024).toFixed(0)} KB · {a.content_type}</div>
                    </div>
                  </div>
                  <Download className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        )}

        <section className="mb-12">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="overline">§ QUOTES ({quotes.length})</div>
            {isBuyer && quotes.length > 0 && (
              <button onClick={evaluateBids} disabled={evaluating} className="btn-primary text-xs py-2 px-4" data-testid="evaluate-bids-btn">
                <Gavel className="w-3.5 h-3.5" /> {evaluating ? "Evaluating…" : "Evaluate bids with AI"}
              </button>
            )}
          </div>

          {bidEval && (
            <div className="editorial-card p-6 mb-6 border-l-4 border-l-klein" data-testid="bid-eval-results">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-5 h-5 text-klein" />
                <div className="overline">AI VERDICT</div>
              </div>
              <p className="text-base leading-relaxed mb-5">Quotes are ranked by price and lead time. Manufacturer identities are hidden in this evaluation.</p>
              <div className="space-y-3">
                {(bidEval.ranked || []).map((r, i) => (
                  <div key={r.quote_id} className={`p-4 border ${r.quote_id === bidEval.winner_quote_id ? "border-klein bg-klein/5" : "border-[--border-soft]"}`} data-testid={`bid-rank-${i}`}>
                    <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
                      <div className="flex items-center gap-3">
                        <span className="font-serif text-3xl tracking-tight">#{i + 1}</span>
                        <span className="font-serif text-xl">Quote {i + 1}</span>
                        {r.quote_id === bidEval.winner_quote_id && <span className="tag verified">WINNER</span>}
                      </div>
                      <div className="flex items-center gap-3 font-mono text-xs">
                        <span>SCORE {r.score}</span>
                        <span className={`tag ${r.verdict === "strong" ? "verified" : r.verdict === "weak" ? "accent" : ""}`}>{r.verdict}</span>
                      </div>
                    </div>
                    <p className="text-sm text-[--muted-foreground] mb-2">Ranked by quote pricing and lead time.</p>
                  </div>
                ))}
              </div>
              {bidEval.error && <div className="mt-4 font-mono text-[10px] text-[--muted-foreground]">fallback: {bidEval.error}</div>}
            </div>
          )}

          <div className="space-y-4">
            {quotes.map((q) => {
              const isWinner = q.status === "won" || rfq.winner_quote_id === q.quote_id || ["confirmed","packed","dispatched","delivered"].includes(q.status);
              const notSelected = q.status === "not_selected";
              const mine = user?.user_id === q.agent_user_id;
              const STATUS_FLOW = ["won", "confirmed", "packed", "dispatched", "delivered"];
              const idx = STATUS_FLOW.indexOf(q.status);
              const inFulfilment = idx >= 0;
              return (
                <div key={q.quote_id} className={`editorial-card p-6 ${isWinner ? "border-l-4 border-l-klein bg-klein/5" : notSelected ? "opacity-60" : ""}`} data-testid={`quote-${q.quote_id}`}>
                  <div className="flex justify-between items-baseline flex-wrap gap-3 mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {q.agent?.company_name === "Anonymous bidder" || (q.agent_user_id === "hidden") ? (
                        <span className="font-serif text-2xl leading-tight italic text-[--muted-foreground]" data-testid={`anon-vendor-${q.quote_id}`}>{q.agent?.company_name || "Anonymous bidder"}</span>
                      ) : user?.role === "admin" ? (
                        <Link to={`/manufacturers/${q.agent_id}`} className="font-serif text-2xl leading-tight">{q.agent?.company_name || "Manufacturer"}</Link>
                      ) : (
                        <span className="font-serif text-2xl leading-tight" data-testid={`revealed-vendor-${q.quote_id}`}>{q.agent?.company_name || "Manufacturer"}</span>
                      )}
                      <VendorBadges badges={q.agent?.badges || []} />
                      {q.agent?.verified && q.agent_user_id === "hidden" && <span className="tag verified">VERIFIED</span>}
                      {isWinner && <span className="tag verified flex items-center gap-1"><Trophy className="w-3 h-3" />WINNER</span>}
                      {notSelected && <span className="tag">NOT SELECTED</span>}
                      {mine && isWinner && <span className="tag accent">YOU WON 🏆</span>}
                      {isBuyer && q.shortlisted && <span className="tag accent" data-testid={`shortlisted-tag-${q.quote_id}`}>SHORTLISTED</span>}
                    </div>
                    <div className="font-mono text-xs text-[--muted-foreground]">{new Date(q.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="flex gap-6 mb-4 flex-wrap">
                    <div><div className="overline">Price / unit</div><div className="font-serif text-2xl">₹{q.price_usd}</div></div>
                    <div><div className="overline">Lead time</div><div className="font-serif text-2xl">{q.lead_time_days}d</div></div>
                    {q.sample_available && q.sample_pricing_mode !== "per_sku" && (
                      <>
                        <div><div className="overline">Sample cost</div><div className="font-serif text-2xl">₹{q.sample_cost_usd || 0}</div></div>
                        {q.sample_lead_time_days != null && (
                          <div><div className="overline">Sample lead time</div><div className="font-serif text-2xl">{q.sample_lead_time_days}d</div></div>
                        )}
                      </>
                    )}
                    {q.sample_available && q.sample_pricing_mode === "per_sku" && (
                      <div><div className="overline">Sample</div><div className="font-serif text-2xl">Per-SKU</div></div>
                    )}
                  </div>
                  {q.message && <p className="text-sm whitespace-pre-wrap mb-4">{q.message}</p>}

                  {(q.sku_quotes || []).length > 0 && (
                    <div className="mb-4 overflow-x-auto" data-testid={`sku-quotes-${q.quote_id}`}>
                      <div className="overline mb-2">PER-SKU PRICING</div>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-[--border-soft] text-left font-mono uppercase tracking-wider text-[--muted-foreground]">
                            <th className="p-2">SKU</th>
                            <th className="p-2">Price / unit</th>
                            <th className="p-2">Lead time</th>
                            {q.sample_available && q.sample_pricing_mode === "per_sku" && <th className="p-2">Sample cost</th>}
                            <th className="p-2">Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {q.sku_quotes.map((sq, i) => (
                            <tr key={sq.sku || i} className="border-b border-[--border-soft]">
                              <td className="p-2 font-mono">{sq.sku || "—"}</td>
                              <td className="p-2">{sq.price_usd != null ? `₹${sq.price_usd}` : "—"}</td>
                              <td className="p-2">{sq.lead_time_days != null ? `${sq.lead_time_days}d` : "—"}</td>
                              {q.sample_available && q.sample_pricing_mode === "per_sku" && <td className="p-2">{sq.sample_cost_usd != null ? `₹${sq.sample_cost_usd}` : "—"}</td>}
                              <td className="p-2 text-[--muted-foreground]">{sq.notes || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {inFulfilment && (
                    <div className="mb-4 p-4 bg-bone border border-[--border-soft]" data-testid={`status-flow-${q.quote_id}`}>
                      <div className="overline mb-3 flex items-center gap-2"><Truck className="w-3 h-3" /> ORDER STATUS</div>
                      <div className="flex items-center gap-1">
                        {STATUS_FLOW.map((s, i) => (
                          <React.Fragment key={s}>
                            <div className={`flex-1 text-center py-2 px-1 font-mono text-[10px] uppercase tracking-wider ${i <= idx ? "bg-klein text-white" : "bg-[--muted] text-[--muted-foreground]"}`}>{s}</div>
                            {i < STATUS_FLOW.length - 1 && <div className={`w-1 h-1 ${i < idx ? "bg-klein" : "bg-[--border-soft]"}`}></div>}
                          </React.Fragment>
                        ))}
                      </div>
                      {q.tracking_url && (
                        <a href={q.tracking_url} target="_blank" rel="noreferrer" className="font-mono text-[10px] underline mt-3 inline-block">TRACK SHIPMENT →</a>
                      )}
                    </div>
                  )}

                  <div className="flex gap-2 flex-wrap">
                    {user && user.user_id !== q.agent_user_id && q.agent_user_id !== "hidden" && (
                      <button onClick={() => nav(`/messages/${q.agent_user_id}`)} className="btn-outline text-xs" data-testid={`quote-chat-${q.quote_id}`}>
                        <MessageSquare className="w-3 h-3" /> Message
                      </button>
                    )}
                    {isBuyer && !isWinner && !notSelected && (
                      <button
                        onClick={() => toggleShortlist(q.quote_id, !q.shortlisted)}
                        disabled={shortlisting === q.quote_id}
                        className={q.shortlisted ? "btn-primary text-xs" : "btn-outline text-xs"}
                        data-testid={`shortlist-quote-${q.quote_id}`}
                      >
                        <Trophy className="w-3 h-3" /> {shortlisting === q.quote_id ? "…" : q.shortlisted ? "Shortlisted ✓" : "Shortlist"}
                      </button>
                    )}
                    {isBuyer && q.sample_available && (q.shortlisted || isWinner) && (
                      samples.some((sample) => sample.quote_id === q.quote_id) ? (
                        <span className="tag verified" data-testid={`sample-requested-${q.quote_id}`}>
                          SAMPLE {samples.find((sample) => sample.quote_id === q.quote_id)?.status?.replaceAll("_", " ")}
                        </span>
                      ) : (
                        <button
                          onClick={() => requestSample(q)}
                          disabled={sampleBusy === q.quote_id}
                          className="btn-outline text-xs"
                          data-testid={`request-sample-${q.quote_id}`}
                        >
                          {sampleBusy === q.quote_id ? "Requesting…" : "Request sample at quoted cost"}
                        </button>
                      )
                    )}
                    {isBuyer && rfq.status === "open" && (
                      <button onClick={() => acceptQuote(q.quote_id)} disabled={accepting === q.quote_id} className="btn-primary text-xs" data-testid={`accept-quote-${q.quote_id}`}>
                        <Trophy className="w-3 h-3" /> {accepting === q.quote_id ? "Accepting…" : "Accept & close RFQ"}
                      </button>
                    )}
                    {/* Agent (winner) status update buttons */}
                    {isAgent && mine && q.status === "won" && (
                      <button onClick={() => updateStatus(q.quote_id, "confirmed")} disabled={statusUpdating === q.quote_id} className="btn-primary text-xs" data-testid={`mark-confirmed-${q.quote_id}`}>Mark confirmed</button>
                    )}
                    {isAgent && mine && q.status === "confirmed" && (
                      <button onClick={() => updateStatus(q.quote_id, "packed")} disabled={statusUpdating === q.quote_id} className="btn-primary text-xs" data-testid={`mark-packed-${q.quote_id}`}>Mark packed</button>
                    )}
                    {isAgent && mine && q.status === "packed" && (
                      <div className="flex gap-2 items-center flex-wrap">
                        <input type="url" placeholder="Tracking URL (optional)" className="input-underline text-xs px-3 py-2 border" value={trackingUrl} onChange={(e) => setTrackingUrl(e.target.value)} data-testid={`tracking-input-${q.quote_id}`} />
                        <button onClick={() => updateStatus(q.quote_id, "dispatched", trackingUrl)} disabled={statusUpdating === q.quote_id} className="btn-primary text-xs" data-testid={`mark-dispatched-${q.quote_id}`}>Mark dispatched</button>
                      </div>
                    )}
                    {/* Buyer marks delivered */}
                    {isBuyer && q.status === "dispatched" && (
                      <button onClick={() => updateStatus(q.quote_id, "delivered")} disabled={statusUpdating === q.quote_id} className="btn-accent text-xs" data-testid={`mark-delivered-${q.quote_id}`}>
                        <Truck className="w-3 h-3" /> Confirm delivery
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {quotes.length === 0 && <div className="text-sm text-[--muted-foreground] font-mono">— No quotes yet.</div>}
          </div>
        </section>

        {isAgent && biddingOpen && (
          <form onSubmit={submitQuote} className="editorial-card p-8" data-testid="quote-form">
            <div className="overline mb-4">SUBMIT A QUOTE</div>

            {skuList.length > 0 ? (
              <div className="mb-6" data-testid="quote-sku-pricing">
                <div className="overline mb-2">PER-SKU PRICING (required)</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[--border-soft] text-left font-mono text-[10px] uppercase tracking-wider text-[--muted-foreground]">
                        <th className="p-2">SKU</th>
                        <th className="p-2">Product</th>
                        <th className="p-2">Price / unit (₹)</th>
                        <th className="p-2">Lead time (d)</th>
                        {quote.sample_available && quote.sample_pricing_mode === "per_sku" && <th className="p-2">Sample cost (₹)</th>}
                        <th className="p-2">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {skuList.map((s, i) => {
                        const row = quote.sku_quotes[i] || { sku: s.sku, price_usd: "", lead_time_days: "", sample_cost_usd: "", notes: "" };
                        const setRow = (patch) => {
                          const next = skuList.map((sk, idx) => quote.sku_quotes[idx] || { sku: sk.sku, price_usd: "", lead_time_days: "", sample_cost_usd: "", notes: "" });
                          next[i] = { ...next[i], ...patch };
                          setQuote({ ...quote, sku_quotes: next });
                        };
                        return (
                          <tr key={s.sku || i} className="border-b border-[--border-soft]">
                            <td className="p-2 font-mono">{s.sku || "—"}</td>
                            <td className="p-2">{s.product_name || "—"}</td>
                            <td className="p-2"><input required type="number" min="0" className="input-underline text-sm" value={row.price_usd} onChange={(e) => setRow({ price_usd: e.target.value === "" ? "" : Number(e.target.value) })} data-testid={`quote-sku-price-${i}`} /></td>
                            <td className="p-2"><input required type="number" min="0" className="input-underline text-sm" value={row.lead_time_days} onChange={(e) => setRow({ lead_time_days: e.target.value === "" ? "" : Number(e.target.value) })} data-testid={`quote-sku-lead-${i}`} /></td>
                            {quote.sample_available && quote.sample_pricing_mode === "per_sku" && (
                              <td className="p-2"><input required type="number" min="0" className="input-underline text-sm" value={row.sample_cost_usd} onChange={(e) => setRow({ sample_cost_usd: e.target.value === "" ? "" : Number(e.target.value) })} data-testid={`quote-sku-sample-cost-${i}`} /></td>
                            )}
                            <td className="p-2"><input className="input-underline text-sm" value={row.notes} onChange={(e) => setRow({ notes: e.target.value })} data-testid={`quote-sku-notes-${i}`} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <div className="overline mb-1">Price per unit (₹)</div>
                  <input type="number" required className="input-underline" value={quote.price_usd} onChange={(e) => setQuote({ ...quote, price_usd: e.target.value })} data-testid="quote-price" />
                </div>
                <div>
                  <div className="overline mb-1">Lead time (days)</div>
                  <input type="number" required className="input-underline" value={quote.lead_time_days} onChange={(e) => setQuote({ ...quote, lead_time_days: e.target.value })} data-testid="quote-lead" />
                </div>
              </div>
            )}

            <div className="mb-6">
              <label className="flex items-center gap-3 cursor-pointer mb-4">
                <input type="checkbox" checked={!!quote.sample_available} onChange={(e) => setQuote({ ...quote, sample_available: e.target.checked })} data-testid="quote-sample-toggle" />
                <span className="text-sm">I can ship a sample</span>
              </label>

              {quote.sample_available && (
                <div className="pl-7 space-y-4">
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="radio" name="sample_pricing_mode" checked={quote.sample_pricing_mode === "standard"} onChange={() => setQuote({ ...quote, sample_pricing_mode: "standard" })} data-testid="quote-sample-mode-standard" />
                      Standard sample cost &amp; lead time
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="radio" name="sample_pricing_mode" checked={quote.sample_pricing_mode === "per_sku"} onChange={() => setQuote({ ...quote, sample_pricing_mode: "per_sku" })} data-testid="quote-sample-mode-per-sku" />
                      Per-SKU sample cost
                    </label>
                  </div>
                  {quote.sample_pricing_mode === "standard" && (
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <div className="overline mb-1">Sample cost (₹)</div>
                        <input required type="number" min="0" className="input-underline" value={quote.sample_cost_usd} onChange={(e) => setQuote({ ...quote, sample_cost_usd: e.target.value })} data-testid="quote-sample-cost" />
                      </div>
                      <div>
                        <div className="overline mb-1">Sample lead time (days)</div>
                        <input required type="number" min="0" className="input-underline" value={quote.sample_lead_time_days} onChange={(e) => setQuote({ ...quote, sample_lead_time_days: e.target.value })} data-testid="quote-sample-lead" />
                      </div>
                    </div>
                  )}
                  {quote.sample_pricing_mode === "per_sku" && (
                    <p className="text-xs text-[--muted-foreground]">Enter each SKU's sample cost in the pricing table above.</p>
                  )}
                </div>
              )}
            </div>

            <div className="mb-6">
              <div className="overline mb-1">Approach / pitch</div>
              <textarea required className="input-underline min-h-[100px]" value={quote.message} onChange={(e) => setQuote({ ...quote, message: e.target.value })} data-testid="quote-message" placeholder="Raw materials, MOQ-fit, capacity, why you're a fit…" />
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[--muted-foreground] mb-3 leading-relaxed" data-testid="commission-microcopy">
              By submitting this quote, you agree to AskWinn's standard success fee if the buyer accepts and initiates production. Payment is held in escrow and released to you on delivery confirmation.
            </div>
            <button type="submit" className="btn-primary" data-testid="quote-submit">Submit quote</button>
          </form>
        )}
        {isAgent && rfq.status === "open" && !biddingOpen && (
          <div className="editorial-card p-8 font-mono text-sm text-[--muted-foreground]">
            — Bidding is currently {rfq.bidding_status === "paused" ? "paused" : "closed"}{rfq.bidding_deadline && new Date(rfq.bidding_deadline) <= new Date() ? " because the deadline has passed" : ""}.
          </div>
        )}
        {isAgent && rfq.status === "closed" && (
          <div className="editorial-card p-8 border-l-4 border-l-klein">
            <div className="overline mb-2">§ CLOSED</div>
            <p className="text-sm">This RFQ has been closed by the buyer. Check the quotes above to see the outcome.</p>
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ l, v }) => (
  <div className="editorial-card p-6">
    <div className="overline mb-2">{l}</div>
    <div className="font-serif text-3xl tracking-tight">{v}</div>
  </div>
);
